import type { ActionContext, ActionTree } from 'vuex';
import type { ITSIA2State } from '@/store/modules/tsia2/types';
import type { IAnswer, RootState } from '@/store/types';
import api from '@/ApiCaller';
import { getTrpcClient, trpcCall } from '@/trpc';
import type { ITSIEssayPayload } from '@/store/modules/tsi/types';
import type { ILockSection } from '../assessment/types';

type TSIA2ActionContext = ActionContext<ITSIA2State, RootState>;
type TSIA2ActionTree = ActionTree<ITSIA2State, RootState>;
type NextQuestionId = { next_question_id: number };

export const actions: TSIA2ActionTree = {
  async fetchTSIA2Windows(context: TSIA2ActionContext): Promise<any> {
    const { data } = await api.get('tsi-homeroom/sections');

    // Batch-resolve unqids for all sections in windows
    const sections: { ref: any; id: number }[] = [];
    for (const win of data as any[]) {
      if (!win.sections) continue;
      for (const subjectKey of Object.keys(win.sections)) {
        const subject = win.sections[subjectKey];
        if (subjectKey === 'essay') {
          if (subject?.id) sections.push({ ref: subject, id: subject.id });
        } else if (subject) {
          for (const typeKey of Object.keys(subject)) {
            const sec = subject[typeKey];
            if (sec?.id) sections.push({ ref: sec, id: sec.id });
          }
        }
      }
    }

    const resolved = await Promise.allSettled(
      sections.map(s =>
        trpcCall(() => getTrpcClient().review.resolveUnqid.query({ usection_id: s.id }))
          .then((r: any) => ({ id: s.id, unqid: r.unqid }))
      )
    );
    const unqidMap = new Map<number, string>();
    for (const r of resolved) {
      if (r.status === 'fulfilled' && r.value.unqid) {
        unqidMap.set(r.value.id, r.value.unqid);
      }
    }
    for (const s of sections) {
      const unqid = unqidMap.get(s.id);
      if (unqid) s.ref.unqid = unqid;
    }

    context.commit('setTSIA2Windows', data);
  },

  async reviewSection(context: TSIA2ActionContext, params: any): Promise<any> {
    await context.dispatch('resetSectionState');

    if (params.unqid) {
      const trpcData = await trpcCall(() =>
        (getTrpcClient() as any).review.loadSection.query({ unqid: params.unqid })
      ) as any;
      context.commit('setTSIA2Section', { ...trpcData, unqid: params.unqid });
      return;
    }

    // Fallback to old REST path for sections without unqid
    const { data } = await api.get(
      `tsi-assessment/take-section/${params.subject}/${params.testNumber}/${params.testType}`
    );
    context.commit('setTSIA2Section', data);
  },

  async takeSection(context: TSIA2ActionContext, params: any): Promise<any> {
    await context.dispatch('resetSectionState');

    if (params.unqid) {
      const trpcData = await trpcCall(() =>
        getTrpcClient().assessment.takeSection.query({ unqid: params.unqid })
      ) as any;
      context.commit('setTSIA2Section', { ...trpcData, unqid: params.unqid });
      context.commit('setTSIA2NextQuestionId', trpcData.next_question_id || null);
      return;
    }

    const { data } = await api.get<NextQuestionId>(
      `tsi-assessment/take-section/${params.subject}/${params.testNumber}/${params.testType}`
    );

    context.commit('setTSIA2Section', data);
    context.commit('setTSIA2NextQuestionId', data.next_question_id || null);

    // Resolve unqid for tRPC review actions
    if ((data as any).id && !(data as any).unqid) {
      try {
        const result = await trpcCall(() =>
          getTrpcClient().review.resolveUnqid.query({ usection_id: (data as any).id })
        ) as { unqid: string };
        if (result.unqid) {
          context.commit('setTSIA2Section', { ...data, unqid: result.unqid });
        }
      } catch { /* non-blocking — falls back to old REST */ }
    }
  },

  async saveAnswer(context: TSIA2ActionContext, payload: IAnswer): Promise<any> {
    const unqid = context.state.section?.unqid;
    if (unqid) {
      const result = await trpcCall(() =>
        getTrpcClient().assessment.saveAnswer.mutate({
          unqid,
          question_id: payload.question_id,
          question_num: payload.question_num,
          time_spent: payload.time_spent,
          time_remain: payload.time_remain,
          answer: payload.answer,
          flagged: !!payload.flagged,
        })
      ) as any;
      context.commit('setTSIA2NextQuestionId', result.next_question_id || null);
      return;
    }

    const { data } = await api.post<NextQuestionId, IAnswer>('tsi-assessment/save-answer', payload);
    context.commit('setTSIA2NextQuestionId', data.next_question_id || null);
  },

  async finishSection(context: TSIA2ActionContext, payload: { usectionId: number }): Promise<any> {
    const unqid = context.state.section?.unqid;
    if (unqid) {
      await trpcCall(() =>
        getTrpcClient().assessment.finishSection.mutate({
          unqid,
          isForceFinished: false,
          forceFinishedBy: 0,
          isOffline: false,
        })
      );
      const { section } = context.state;
      if (section) context.commit('setTSIA2Section', { ...section, is_completed: 1 });
      return;
    }

    await api.post('tsi-assessment/finish-section', { usection_id: payload.usectionId });
    const { section } = context.state;
    if (section) context.commit('setTSIA2Section', { ...section, is_completed: 1 });
  },

  async fetchPctReviewed(context: TSIA2ActionContext, params: any): Promise<any> {
    const unqid = context.state.section?.unqid;
    if (unqid) {
      const data = await trpcCall(() =>
        getTrpcClient().review.getPctReviewed.query({ unqid })
      );
      context.commit('setPctReviewed', data);
      return;
    }

    const pctReviewed = await api.get(
      `tsi-assessment/pct-reviewed/${params.subject}/${params.testNumber}/${params.testType}`
    );
    context.commit('setPctReviewed', pctReviewed.data);
  },

  async fetchAnsweredQuestions(context: TSIA2ActionContext, params: any): Promise<any> {
    const unqid = context.state.section?.unqid;
    if (unqid) {
      const data = await trpcCall(() =>
        getTrpcClient().review.getReviewQuestions.query({ unqid })
      );
      context.commit('setAnsweredQuestions', data);
      return;
    }

    const content = await api.get(
      `/tsi-assessment/review-questions/${params.subject}/${params.testNumber}/${params.testType}`
    );
    context.commit('setAnsweredQuestions', content.data);
  },

  async reviewQuestion(context: TSIA2ActionContext, payload: any): Promise<any> {
    const unqid = context.state.section?.unqid;
    if (unqid) {
      await trpcCall(() =>
        getTrpcClient().review.questionViewed.mutate({
          unqid,
          question_id: payload.question_id,
          question_num: payload.num ?? 0,
        })
      );
      return;
    }

    await api.post('/tsi-assessment/question-viewed', payload);
  },

  async handleQuestionFeedback(context: TSIA2ActionContext, payload: any): Promise<any> {
    const unqid = context.state.section?.unqid;
    if (unqid) {
      await trpcCall(() =>
        getTrpcClient().review.postQuestionFeedback.mutate({
          unqid,
          question_id: payload.question_id,
          question_num: payload.question_num ?? 0,
          feedback: payload.feedback as 'i' | 'c',
        })
      );
      return;
    }

    await api.post('/tsi-assessment/question-feedback', payload);
  },

  async fetchQuestionLessons(context: TSIA2ActionContext, question_id: number): Promise<any> {
    const data = await trpcCall(() =>
      getTrpcClient().review.getQuestionLessons.query({ question_id })
    );
    context.commit('setQuestionLessons', data);
  },

  async fetchEssay(context: TSIA2ActionContext, testNumber: number): Promise<any> {
    const content = await api.get(`/tsi-assessment/take-section/essay/${testNumber}`);
    context.commit('setEssay', content.data);
  },

  async lockSection(context: TSIA2ActionContext, payload: ILockSection): Promise<void> {
    await api.post(`${context.rootState.productPrefix}assessment/lock-section`, {
      usection_id: payload.usection_id,
      is_locked: payload.is_locked,
      locked_by: payload.locked_by,
    });
  },

  async ksaFetchEssay(context: TSIA2ActionContext, testNumber: number): Promise<any> {
    const content = await api.get(`/courses-assessment/take-section/essay/${testNumber}`);
    context.commit('setEssay', content.data);
  },

  async saveEssayText(context: TSIA2ActionContext, essayText: string): Promise<any> {
    context.commit('setEssayText', essayText);
  },

  async saveEssay(_: TSIA2ActionContext, payload: ITSIEssayPayload): Promise<any> {
    await api.post('tsi-assessment/save-essay', payload);
  },

  async ksaSaveEssay(_: TSIA2ActionContext, payload: ITSIEssayPayload): Promise<any> {
    await api.post('courses-assessment/save-essay', payload);
  },

  async gradeEssay(_: TSIA2ActionContext, payload: ITSIEssayPayload): Promise<any> {
    await api.post('tsi-assessment/grade-essay', payload);
  },

  async ksaGradeEssay(_: TSIA2ActionContext, payload: ITSIEssayPayload): Promise<any> {
    await api.post('courses-assessment/grade-essay', payload);
  },

  async resetSectionState(context: TSIA2ActionContext): Promise<any> {
    context.commit('setDefaultSectionState');
  },
};
